import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Expedition } from '../types';
import { Calendar } from 'lucide-react';
import { createPortal } from 'react-dom';
import PolicyText from './PolicyText';
import { Language, WELLNESS_SERVICES } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface BookingWidgetProps {
    exp: Expedition;
    t: any;
    lang: Language;
    selectedVariantId?: string;
    onVariantChange?: (id: string) => void;
    selectedServices?: string[];
    onServiceToggle?: (service: string) => void;
}

const BookingWidget: React.FC<BookingWidgetProps> = ({ 
    exp, 
    t, 
    lang,
    selectedVariantId: propVariantId, 
    onVariantChange,
    selectedServices: propSelectedServices,
    onServiceToggle
}) => {
    const navigate = useNavigate();
    const [people, setPeople] = useState(1);
    const [airport, setAirport] = useState(false);
    const [showPolicy, setShowPolicy] = useState(false);
    const [internalVariantId, setInternalVariantId] = useState(exp.variants?.[0]?.id || '');
    const [selectedUpgrades, setSelectedUpgrades] = useState<string[]>([]);

    const handleUpgradeToggle = (upgradeName: string) => {
        if (selectedUpgrades.includes(upgradeName)) {
            setSelectedUpgrades(selectedUpgrades.filter(u => u !== upgradeName));
        } else {
            setSelectedUpgrades([...selectedUpgrades, upgradeName]);
        }
    };

    const selectedVariantId = propVariantId !== undefined ? propVariantId : internalVariantId;
    const setSelectedVariantId = (id: string) => {
        if (onVariantChange) onVariantChange(id);
        else setInternalVariantId(id);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowPolicy(false);
            }
        };

        if (showPolicy) {
            document.addEventListener('keydown', handleKeyDown);
            // Optionally prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [showPolicy]);

    useEffect(() => {
        if (exp.variants && exp.variants.length > 0) {
            setInternalVariantId(exp.variants[0].id);
        } else {
            setInternalVariantId('');
        }
    }, [exp]);

    const dateInputRef = useRef<HTMLInputElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);

    const prices = useMemo(() => {
        let perPerson = 0;
        let total = 0;

        // 1. If it has a flat price, use that
        const flatPrice = typeof exp.price_flat === 'number' ? exp.price_flat : parseFloat(String(exp.price_flat || ''));
        if (flatPrice && !isNaN(flatPrice) && flatPrice > 0) {
            perPerson = flatPrice;
            total = perPerson * people;
        } else {
            // 2. Otherwise, check tiered pricing
            let pricingTiers = (exp.pricing_tiers && exp.pricing_tiers.length > 0) ? exp.pricing_tiers : null;

            // If it has variants, use the pricing tiers of the selected variant if available
            if (exp.variants && selectedVariantId) {
                const variant = exp.variants.find(v => v.id === selectedVariantId);
                if (variant && variant.pricing_tiers && variant.pricing_tiers.length > 0) {
                    pricingTiers = variant.pricing_tiers;
                }
            }

            if (pricingTiers && pricingTiers.length > 0) {
                // Dynamic tiered pricing
                const sortedTiers = [...pricingTiers].sort((a, b) => b.min_people - a.min_people);
                let applicableTier = sortedTiers.find(tier => people >= tier.min_people);

                // Fallback to lowest tier if people is less than minimum tier
                if (!applicableTier && sortedTiers.length > 0) {
                    applicableTier = sortedTiers[sortedTiers.length - 1];
                }

                if (applicableTier) {
                    perPerson = applicableTier.price_per_person;
                    total = perPerson * people;
                }
            }

            // 3. Fallbacks if no tier matched or no tiers defined
            if (!perPerson) {
                if (exp.id === 'laguna') {
                    if (people === 1) perPerson = 170;
                    else if (people < 4) perPerson = 90;
                    else perPerson = 70;
                    total = perPerson * people;
                } else if (exp.id === 'horquetas') {
                    if (people === 1) perPerson = 900;
                    else if (people === 2) perPerson = 700;
                    else perPerson = 500;
                    total = perPerson * people;
                } else if (exp.price_from && Number(exp.price_from) > 0) {
                    perPerson = Number(exp.price_from);
                    total = perPerson * people;
                } else {
                    return null;
                }
            }
        }

        // Add airport transfer fee
        if (airport) total += 50;

        // Add price upgrades (optional extras)
        let upgradesCost = 0;
        if (exp.price_upgrades && exp.price_upgrades.length > 0) {
            exp.price_upgrades.forEach(upg => {
                if (selectedUpgrades.includes(upg.name_es)) {
                    upgradesCost += upg.price * people;
                }
            });
        }
        total += upgradesCost;

        return { perPerson, total };
    }, [exp, people, airport, selectedVariantId, selectedUpgrades]);

    const [internalSelectedServices, setInternalSelectedServices] = useState<string[]>([]);

    const selectedServices = propSelectedServices !== undefined ? propSelectedServices : internalSelectedServices;

    const handleServiceToggle = (service: string) => {
        if (onServiceToggle) {
            onServiceToggle(service);
        } else {
            if (internalSelectedServices.includes(service)) {
                setInternalSelectedServices(internalSelectedServices.filter(s => s !== service));
            } else if (internalSelectedServices.length < 3) {
                setInternalSelectedServices([...internalSelectedServices, service]);
            }
        }
    };

    const [showInlineError, setShowInlineError] = useState(false);
    const [errors, setErrors] = useState<{date?: boolean, name?: boolean, email?: boolean, services?: boolean}>({});

    const handleSubmit = () => {
        const date = dateInputRef.current?.value;
        const name = nameInputRef.current?.value;
        const email = emailInputRef.current?.value;

        const newErrors = {
            date: !date,
            name: !name,
            email: !email,
            services: exp.id === 'wellness' && selectedVariantId === 'wellness-menu' && selectedServices.length === 0
        };

        if (newErrors.date || newErrors.name || newErrors.email || newErrors.services) {
            setErrors(newErrors);
            setShowInlineError(true);
            setTimeout(() => setShowInlineError(false), 3000);
            return;
        }

        setErrors({});

        // Redirects to secondary registration page mapping the number of participants
        navigate(getLocalizedPath('/registro', lang), { 
            state: { 
                people, 
                expId: exp.id, 
                variantId: selectedVariantId, 
                date, 
                airport,
                total: prices?.total,
                leadName: name,
                leadEmail: email,
                selectedServices: exp.id === 'wellness' ? selectedServices : undefined,
                selectedUpgrades: selectedUpgrades
            } 
        });
    };

    const widgetI18n = useMemo(() => {
        const dict: Record<Language, {
            selected: string;
            choose_activities: string;
            lead_explorer: string;
            lead_placeholder: string;
            lead_email: string;
            lead_email_placeholder: string;
            upgrades: string;
            per_person: string;
            missing_fields: string;
            continue_step_2: string;
            view_policies: string;
            open_tab: string;
            deposit_banner_title: string;
            deposit_banner_desc: string;
            deposit_today: string;
            deposit_balance_note: string;
            btn_deposit: string;
        }> = {
            es: {
                selected: 'Seleccionados',
                choose_activities: 'Elige hasta 3 actividades',
                lead_explorer: 'Líder de Reserva',
                lead_placeholder: 'Nombre completo',
                lead_email: 'Correo de Contacto (Líder)',
                lead_email_placeholder: 'Tu correo',
                upgrades: 'Servicios Adicionales (Opcional)',
                per_person: '/ persona',
                missing_fields: 'Faltan campos obligatorios',
                continue_step_2: 'Seguir al Paso 2 ➔',
                view_policies: 'Ver Políticas y Condiciones',
                open_tab: 'Abrir en pestaña nueva ↗',
                deposit_banner_title: 'Reserva Flexible: Paga solo el 50% hoy',
                deposit_banner_desc: 'Asegura tu fecha con un 50% de abono. El saldo restante se cancela 48 hrs antes de la expedición.',
                deposit_today: 'Pagas hoy para reservar (50%):',
                deposit_balance_note: 'Saldo restante del 50% a pagar 48 hrs antes:',
                btn_deposit: 'Reservar con 50% de Abono'
            },
            en: {
                selected: 'Selected',
                choose_activities: 'Choose up to 3 activities',
                lead_explorer: 'Lead Explorer',
                lead_placeholder: 'Full name',
                lead_email: 'Lead Contact Email',
                lead_email_placeholder: 'Email address',
                upgrades: 'Optional Upgrades',
                per_person: '/ person',
                missing_fields: 'Missing required fields',
                continue_step_2: 'Continue to Step 2 ➔',
                view_policies: 'View Policies & Terms',
                open_tab: 'Open in new tab ↗',
                deposit_banner_title: 'Flexible Booking: Pay only 50% today',
                deposit_banner_desc: 'Lock in your departure date with a 50% deposit. The remaining balance is due 48 hours before the expedition.',
                deposit_today: 'Pay today to reserve (50%):',
                deposit_balance_note: 'Remaining 50% balance due 48h before tour:',
                btn_deposit: 'Book with 50% Deposit'
            },
            pt: {
                selected: 'Selecionados',
                choose_activities: 'Escolha até 3 atividades',
                lead_explorer: 'Líder da Reserva',
                lead_placeholder: 'Nome completo',
                lead_email: 'E-mail de Contato (Líder)',
                lead_email_placeholder: 'Seu e-mail',
                upgrades: 'Serviços Opcionais',
                per_person: '/ pessoa',
                missing_fields: 'Preencha os campos obrigatórios',
                continue_step_2: 'Avançar para o Passo 2 ➔',
                view_policies: 'Ver Políticas e Condições',
                open_tab: 'Abrir em nova aba ↗',
                deposit_banner_title: 'Reserva Flexível: Pague apenas 50% hoje',
                deposit_banner_desc: 'Garanta sua data com 50% de entrada. O saldo restante é pago 48 horas antes da expedição.',
                deposit_today: 'Pague hoje para reservar (50%):',
                deposit_balance_note: 'Saldo restante de 50% a pagar 48h antes:',
                btn_deposit: 'Reservar com 50% de Entrada'
            },
            fr: {
                selected: 'Sélectionnés',
                choose_activities: 'Choisissez jusqu’à 3 activités',
                lead_explorer: 'Responsable de la Réservation',
                lead_placeholder: 'Nom complet',
                lead_email: 'E-mail de Contact (Responsable)',
                lead_email_placeholder: 'Votre adresse e-mail',
                upgrades: 'Options Supplémentaires',
                per_person: '/ personne',
                missing_fields: 'Champs obligatoires manquants',
                continue_step_2: 'Passer à l’étape 2 ➔',
                view_policies: 'Voir les Conditions Générales',
                open_tab: 'Ouvrir dans un nouvel onglet ↗',
                deposit_banner_title: 'Réservation Flexible : Payez seulement 50% aujourd\'hui',
                deposit_banner_desc: 'Sécurisez votre date avec un acompte de 50%. Le solde restant est réglé 48 heures avant l\'expédition.',
                deposit_today: 'Payez aujourd\'hui pour réserver (50%) :',
                deposit_balance_note: 'Solde restant de 50% dû 48h avant le départ :',
                btn_deposit: 'Réserver avec 50% d\'Acompte'
            },
            it: {
                selected: 'Selezionati',
                choose_activities: 'Scegli fino a 3 attività',
                lead_explorer: 'Capogruppo',
                lead_placeholder: 'Nome completo',
                lead_email: 'Email di Contatto (Capogruppo)',
                lead_email_placeholder: 'La tua email',
                upgrades: 'Servizi Aggiuntivi (Opzionale)',
                per_person: '/ persona',
                missing_fields: 'Campi obbligatori mancanti',
                continue_step_2: 'Procedi al Passaggio 2 ➔',
                view_policies: 'Visualizza Termini e Condizioni',
                open_tab: 'Apri in una nuova scheda ↗',
                deposit_banner_title: 'Prenotazione Flessibile: Paga solo il 50% oggi',
                deposit_banner_desc: 'Assicura la tua data con il 50% di acconto. Il saldo rimanente si paga 48 ore prima della spedizione.',
                deposit_today: 'Paga oggi per prenotare (50%):',
                deposit_balance_note: 'Saldo restante del 50% da pagare 48h prima:',
                btn_deposit: 'Prenota con il 50% di Acconto'
            },
            de: {
                selected: 'Ausgewählt',
                choose_activities: 'Wähle bis zu 3 Aktivitäten',
                lead_explorer: 'Hauptkontakt',
                lead_placeholder: 'Vollständiger Name',
                lead_email: 'Kontakt-E-Mail (Hauptkontakt)',
                lead_email_placeholder: 'Ihre E-Mail',
                upgrades: 'Zusatzleistungen (Optional)',
                per_person: '/ Person',
                missing_fields: 'Erforderliche Felder fehlen',
                continue_step_2: 'Weiter zu Schritt 2 ➔',
                view_policies: 'Richtlinien & Bedingungen anzeigen',
                open_tab: 'In neuem Tab öffnen ↗',
                deposit_banner_title: 'Flexible Buchung: Heute nur 50% anbezahlen',
                deposit_banner_desc: 'Sichern Sie sich Ihren Termin mit 50% Anzahlung. Der Restbetrag ist 48 Stunden vor der Tour fällig.',
                deposit_today: 'Heute anbezahlen (50%):',
                deposit_balance_note: 'Restliche 50% fällig 48h vor der Tour:',
                btn_deposit: 'Mit 50% Anzahlung buchen'
            }
        };
        return dict[lang] || dict.es;
    }, [lang]);

    const wellnessServicesList = useMemo(() => {
        if (exp.id === 'wellness' || exp.slug === 'wellness-day') {
            const list = WELLNESS_SERVICES[lang];
            if (list) return list.map(s => s.name);
        }
        return (lang === 'en' ? exp.services_en : exp.services_es) || [];
    }, [exp.id, exp.slug, exp.services_en, exp.services_es, lang]);

    const bookingVariantLabel = useMemo(() => {
        const labels: Record<string, Record<Language, string>> = {
            climbing: {
                es: 'Tipo de Escalada',
                en: 'Climbing Type',
                pt: 'Tipo de Escalada',
                fr: 'Type d\'Escalade',
                it: 'Tipo di Scalata',
                de: 'Klettertyp'
            },
            trekking: {
                es: 'Opción de Ruta',
                en: 'Route Option',
                pt: 'Opção de Rota',
                fr: 'Option d\'Itinéraire',
                it: 'Opzione Percorso',
                de: 'Routenoption'
            },
            default: {
                es: 'Tipo de Opción',
                en: 'Option Type',
                pt: 'Tipo de Opção',
                fr: 'Type d\'Option',
                it: 'Tipo di Opzione',
                de: 'Optionstyp'
            }
        };
        const cat = exp.category === 'climbing' || exp.category === 'trekking' ? exp.category : 'default';
        return labels[cat][lang] || labels[cat]['es'];
    }, [exp.category, lang]);

    return (
        <div className="bg-stone p-4 sm:p-8 border border-rock shadow-2xl space-y-6 max-w-full">
            <h3 className="font-title text-3xl sm:text-4xl mb-4 border-b border-rock pb-4 uppercase">{t.booking_title}</h3>

            {/* 50% DEPOSIT NOTICE (STEP 1) */}
            <div className="bg-black/50 border border-rock p-4 rounded-sm mb-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00d4ff] mb-1">
                    {widgetI18n.deposit_banner_title}
                </div>
                <p className="text-xs text-gray-300 font-light leading-relaxed font-sans">
                    {widgetI18n.deposit_banner_desc}
                </p>
            </div>
            <div className="space-y-4">
                {exp.variants && exp.id !== 'wellness' && (
                    <div>
                        <label className="text-[9px] uppercase tracking-widest font-bold mb-2 block text-[#00d4ff]">{bookingVariantLabel}</label>
                        <select
                            value={selectedVariantId}
                            onChange={(e) => setSelectedVariantId(e.target.value)}
                            className="w-full h-12 bg-black border border-rock text-white px-4 text-[10px] outline-none focus:border-[#00d4ff] hover:border-gray-500 transition-colors uppercase font-bold tracking-widest appearance-none cursor-pointer"
                        >
                            {exp.variants.map(v => (
                                <option key={v.id} value={v.id}>{v.name_es}</option>
                            ))}
                        </select>
                    </div>
                )}

                {exp.id === 'wellness' && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex justify-between items-end">
                            <label className="text-[9px] uppercase tracking-widest font-bold text-[#00d4ff]">
                                {t.services_title || 'Diseña tu itinerario'} <span className="text-red-500">*</span>
                            </label>
                            <span className={`text-[8px] font-bold uppercase tracking-widest ${selectedServices.length === 3 ? 'text-green-500' : 'text-gray-500'}`}>
                                {selectedServices.length}/3 {widgetI18n.selected}
                            </span>
                        </div>
                        <div className={`grid grid-cols-1 gap-1.5 p-1 transition-colors ${errors.services ? 'bg-red-500/10 border border-red-500' : ''}`}>
                            {wellnessServicesList.map((service, idx) => (
                                <div 
                                    key={idx}
                                    onClick={() => {
                                        handleServiceToggle(service);
                                        setErrors(prev => ({...prev, services: false}));
                                    }}
                                    className={`group flex items-center justify-between p-3 border cursor-pointer transition-all ${selectedServices.includes(service) ? 'bg-[#00d4ff]/10 border-[#00d4ff]' : 'bg-black border-rock hover:border-gray-500'}`}
                                >
                                    <span className={`text-[10px] uppercase tracking-widest font-bold transition-colors ${selectedServices.includes(service) ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}>
                                        {service}
                                    </span>
                                    <div className={`w-3.5 h-3.5 border flex items-center justify-center transition-all ${selectedServices.includes(service) ? 'bg-[#00d4ff] border-[#00d4ff]' : 'bg-transparent border-gray-700'}`}>
                                        {selectedServices.includes(service) && <span className="text-black text-[8px] font-bold">✓</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {selectedServices.length === 0 && (
                            <p className="text-[8px] text-gray-600 italic uppercase tracking-widest text-center">
                                * {widgetI18n.choose_activities}
                            </p>
                        )}
                    </div>
                )}
                <div>
                    <label className="text-[9px] uppercase tracking-widest font-bold mb-2 block text-[#00d4ff]">{t.booking_people}</label>
                    <div className="flex border border-rock bg-black text-white">
                        <button onClick={() => setPeople(Math.max(1, people - 1))} className="w-12 h-12 flex items-center justify-center text-[#00d4ff] font-bold hover:bg-white/10 transition-colors">-</button>
                        <div className="flex-1 h-12 flex items-center justify-center font-bold text-xl">{people}</div>
                        <button onClick={() => setPeople(people + 1)} className="w-12 h-12 flex items-center justify-center text-[#00d4ff] font-bold hover:bg-white/10 transition-colors">+</button>
                    </div>
                </div>

                <div className="relative group cursor-pointer" onClick={() => dateInputRef.current?.showPicker()}>
                    <label className="text-[9px] uppercase tracking-widest font-bold mb-2 block text-[#00d4ff]">
                        {t.booking_date || 'Fecha de expedición'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            ref={dateInputRef}
                            type="date"
                            onFocus={() => setErrors(prev => ({...prev, date: false}))}
                            onChange={() => setErrors(prev => ({...prev, date: false}))}
                            className={`w-full h-12 bg-black border pl-10 pr-4 text-xs text-white outline-none focus:border-[#00d4ff] appearance-none cursor-pointer transition-colors ${errors.date ? 'border-red-500' : 'border-rock group-hover:border-gray-500'}`}
                            required
                        />
                        <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${errors.date ? 'text-red-500' : 'text-[#00d4ff] group-hover:text-white'}`} />
                    </div>
                </div>

                <div>
                    <label className="text-[9px] uppercase tracking-widest font-bold mb-2 block text-[#00d4ff]">
                        {widgetI18n.lead_explorer} <span className="text-red-500">*</span>
                    </label>
                    <input 
                        ref={nameInputRef} 
                        type="text" 
                        onFocus={() => setErrors(prev => ({...prev, name: false}))}
                        placeholder={widgetI18n.lead_placeholder} 
                        className={`w-full h-12 bg-black border px-4 text-xs text-white placeholder:text-gray-600 outline-none focus:border-[#00d4ff] transition-colors ${errors.name ? 'border-red-500' : 'border-rock hover:border-gray-500'}`} 
                        required 
                    />
                </div>

                <div>
                    <label className="text-[9px] uppercase tracking-widest font-bold mb-2 block text-[#00d4ff]">
                        {widgetI18n.lead_email} <span className="text-red-500">*</span>
                    </label>
                    <input 
                        ref={emailInputRef} 
                        type="email" 
                        onFocus={() => setErrors(prev => ({...prev, email: false}))}
                        placeholder={widgetI18n.lead_email_placeholder} 
                        className={`w-full h-12 bg-black border px-4 text-xs text-white placeholder:text-gray-600 outline-none focus:border-[#00d4ff] transition-colors ${errors.email ? 'border-red-500' : 'border-rock hover:border-gray-500'}`} 
                        required 
                    />
                </div>

                {/* Price Upgrades */}
                {exp.price_upgrades && exp.price_upgrades.length > 0 && (
                    <div className="space-y-3 pt-2">
                        <label className="text-[9px] uppercase tracking-widest font-bold text-[#00d4ff]">
                            {widgetI18n.upgrades}
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                            {exp.price_upgrades.map((upg, idx) => {
                                const upgName = (upg as any)[`name_${lang}`] || (lang === 'en' ? upg.name_en : upg.name_es) || upg.name_es;
                                const isSelected = selectedUpgrades.includes(upg.name_es);
                                return (
                                    <div 
                                        key={idx}
                                        onClick={() => handleUpgradeToggle(upg.name_es)}
                                        className={`group flex items-center justify-between p-3 border cursor-pointer transition-all ${
                                            isSelected ? 'bg-[#00d4ff]/10 border-[#00d4ff]' : 'bg-black border-rock hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className={`text-[10px] uppercase tracking-widest font-bold transition-colors ${
                                                isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'
                                            }`}>
                                                {upgName}
                                            </span>
                                            <span className="text-[8px] text-gray-500 font-bold uppercase mt-0.5">
                                                + USD${upg.price} {widgetI18n.per_person}
                                            </span>
                                        </div>
                                        <div className={`w-3.5 h-3.5 border flex items-center justify-center transition-all ${
                                            isSelected ? 'bg-[#00d4ff] border-[#00d4ff]' : 'bg-transparent border-gray-700'
                                        }`}>
                                            {isSelected && <span className="text-black text-[8px] font-bold">✓</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex items-center space-x-3 py-1 cursor-pointer group" onClick={() => setAirport(!airport)}>
                    <div className={`w-5 h-5 border flex items-center justify-center transition-all ${airport ? 'bg-[#00d4ff] border-[#00d4ff]' : 'bg-black border-gray-400 group-hover:border-[#00d4ff]'}`}>
                        {airport && <span className="text-black font-bold text-[10px]">✓</span>}
                    </div>
                    <span className="text-[10px] text-gray-300 uppercase tracking-widest font-bold group-hover:text-white transition-colors">{t.booking_airport}</span>
                </div>

                {prices && (
                    <div className="border-t border-rock pt-5 mt-6 space-y-3">
                        <div className="flex justify-between items-baseline text-gray-400">
                            <span className="text-[10px] uppercase tracking-widest font-bold">{t.price_per_person}</span>
                            <span className="font-bold text-lg tracking-tight">USD${prices.perPerson}</span>
                        </div>
                        <div className="flex justify-between items-baseline text-gray-400">
                            <span className="text-xs uppercase tracking-widest font-bold">{t.price_total}</span>
                            <span className="text-xl font-title text-gray-300 tracking-tight">USD${prices.total}</span>
                        </div>
                        
                        {/* Deposit Breakdown */}
                        <div className="bg-black/60 border border-[#00d4ff]/30 p-3.5 rounded-sm space-y-1.5 mt-2">
                            <div className="flex justify-between items-baseline">
                                <span className="text-xs font-bold uppercase tracking-wider text-[#00d4ff]">
                                    {widgetI18n.deposit_today}
                                </span>
                                <span className="text-3xl sm:text-4xl font-title text-[#00d4ff] tracking-tight">
                                    USD${Math.round(prices.total * 0.5)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-[10px] text-gray-400 pt-1.5 border-t border-white/10">
                                <span>{widgetI18n.deposit_balance_note}</span>
                                <span className="text-amber-400 font-bold tracking-wider">USD${prices.total - Math.round(prices.total * 0.5)}</span>
                            </div>
                        </div>

                        {exp.show_boxlunch_note && (
                            <p className="text-[9px] text-[#00d4ff] uppercase tracking-widest font-bold text-right mt-1 italic">
                                * {t.price_includes_boxlunch}
                            </p>
                        )}
                    </div>
                )}

                {!prices && <div className="p-6 bg-black border border-rock text-[10px] text-gray-700 text-center italic tracking-widest font-bold uppercase">{t.consult}</div>}

                <div className="relative mt-4">
                    {showInlineError && (
                        <div className="absolute -top-12 left-0 w-full animate-in slide-in-from-bottom-2 duration-300">
                            <div className="bg-red-500 text-white text-[10px] font-bold uppercase tracking-widest py-3 px-4 shadow-xl flex items-center justify-between border-b-2 border-red-700">
                                <span>{widgetI18n.missing_fields}</span>
                                <span className="text-red-200 animate-pulse">!</span>
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        disabled={!prices}
                        onClick={handleSubmit}
                        className="w-full bg-[#f2f2f2] text-black h-16 flex items-center justify-center font-title text-lg sm:text-xl hover:bg-[#00d4ff] disabled:opacity-20 transition-all uppercase tracking-tight shadow-lg hover:shadow-[#00d4ff]/20 px-3 text-center"
                    >
                        {prices ? `${widgetI18n.btn_deposit} (USD$${Math.round(prices.total * 0.5)}) ➔` : widgetI18n.continue_step_2}
                    </button>
                </div>

                <div className="text-center mt-2">
                    <button 
                        onClick={() => setShowPolicy(true)}
                        className="text-[9px] text-gray-500 hover:text-[#00d4ff] underline decoration-1 underline-offset-4 tracking-[0.2em] uppercase transition-colors"
                    >
                        {widgetI18n.view_policies}
                    </button>
                </div>

                {showPolicy && createPortal(
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-[#1a1512] border border-rock max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-10 relative shadow-2xl">
                            <div className="absolute top-5 right-6 flex items-center gap-6">
                                <a 
                                    href="/policies" 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="text-[9px] text-[#00d4ff] hidden sm:block uppercase tracking-widest hover:text-white transition-colors border-b border-[#00d4ff]/30 pb-0.5"
                                >
                                    {widgetI18n.open_tab}
                                </a>
                                <button 
                                    onClick={() => setShowPolicy(false)}
                                    className="text-gray-500 hover:text-white transition-colors text-xl font-light"
                                >
                                    ✕
                                </button>
                            </div>
                            <PolicyText />
                        </div>
                    </div>,
                    document.body
                )}

            </div>
        </div>
    );
};

export default BookingWidget;
